// @ts-nocheck
// bootstrapSync.js
// Purpose: establish baseline caches in a dependency-aware order when FF_SYNC_V2
// is active or the user signs in on a fresh device.
// Strategy:
//   1. Load foundational entities (goals) first so dependent collections can
//      inherit metadata such as color assignments.
//   2. Fetch dependent collections in lightweight parallel stages once their
//      prerequisites are satisfied.
//   3. Skip work when a baseline timestamp already exists to avoid redundant
//      network calls for legitimately empty datasets.

import logger from '../../utils/logger';
import { cacheManager } from '../cacheManager';
import * as goalService from '../services/goalService';
import * as planService from '../services/planService';
import * as taskService from '../services/taskService';
import * as chatService from '../services/chatService';
import * as reviewService from '../services/reviewService';

/**
 * Determine whether a cache baseline needs to be established.
 * Baseline is considered present once a lastSync timestamp has been recorded.
 *
 * @param {string} cacheKey
 * @param {string} userId
 */
function needsBaseline(cacheKey, userId) {
  try {
    return !cacheManager.getLastSyncTimestamp(cacheKey, userId);
  } catch (error) {
    logger.debug('bootstrapSync: failed baseline check (defaulting to fetch)', {
      cacheKey,
      error,
    });
    return true;
  }
}

/**
 * Sequential stage runner that supports abort signalling.
 * @param {Array<() => Promise<void>>} jobs
 * @param {AbortSignal|undefined} signal
 */
async function runSequential(jobs, signal) {
  for (const job of jobs) {
    if (signal?.aborted) {
      const abortError = new DOMException('Aborted', 'AbortError');
      throw abortError;
    }
    await job();
  }
}

/**
 * Run a set of jobs in parallel, respecting abort semantics before dispatch.
 * @param {Array<() => Promise<void>>} jobs
 * @param {AbortSignal|undefined} signal
 */
async function runParallel(jobs, signal) {
  if (signal?.aborted) {
    const abortError = new DOMException('Aborted', 'AbortError');
    throw abortError;
  }
  await Promise.all(jobs.map((job) => job()));
}

/**
 * Bootstrap baseline caches for the signed-in user in a dependency aware order.
 * @param {string} userId
 * @param {{ signal?: AbortSignal }} [options]
 */
export async function bootstrapSync(userId, { signal } = {}) {
  if (!userId) return;

  const stages = [
    {
      name: 'foundations',
      runner: runSequential,
      jobs: [
        async () => {
          if (!needsBaseline('goals', userId)) return;
          logger.info('🌱 bootstrapSync: establishing goals baseline');
          await goalService.getGoals(userId, { forceRefresh: false, signal });
        },
      ],
    },
    {
      name: 'plans-tasks',
      runner: runParallel,
      jobs: [
        async () => {
          if (!needsBaseline('plans', userId)) return;
          logger.info('🗂️ bootstrapSync: establishing plans baseline');
          await planService.getPlans(userId, { forceRefresh: false, signal });
        },
        async () => {
          if (!needsBaseline('tasks', userId)) return;
          logger.info('📝 bootstrapSync: establishing tasks baseline');
          await taskService.getTasks(userId, { forceRefresh: false, signal });
        },
      ],
    },
    {
      name: 'chats',
      runner: runSequential,
      jobs: [
        async () => {
          if (!needsBaseline('chats', userId)) return;
          logger.info('💬 bootstrapSync: establishing chats baseline');
          await chatService.getChats(userId, { forceRefresh: false, signal });
        },
      ],
    },
    {
      name: 'reviews',
      runner: runSequential,
      jobs: [
        async () => {
          if (!needsBaseline('reviews_weekly', userId)) return;
          logger.info('🧾 bootstrapSync: establishing weekly reviews baseline');
          await reviewService.getWeeklyReviews(userId, { forceRefresh: false, signal });
        },
      ],
    },
  ];

  for (const stage of stages) {
    const activeJobs = stage.jobs.filter(Boolean);
    if (activeJobs.length === 0) continue;
    try {
      await stage.runner(activeJobs, signal);
    } catch (error) {
      if (error instanceof DOMException && error.name === 'AbortError') {
        logger.info('bootstrapSync: aborted during stage', stage.name);
        throw error;
      }
      logger.warn('bootstrapSync: stage failed (non-blocking)', {
        stage: stage.name,
        error,
      });
    }
  }
}

export default { bootstrapSync };
